import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Flex,
    Image,
    Text,
    VStack,
} from '@chakra-ui/react';

import keys from 'config';
import { useGetAuthorizedTabsSelector, useGetCompanyInfoSelector } from 'core/redux/selectors/authSelectors';
import routesMapper, { sideBarRoutes } from 'router/routesMapper';
import icons from 'shared/icons';
import { RouteDec } from 'types';
import { useTranslation } from 'helpers/languages/translations';
import niqLogo from 'assets/NIQ-DigitalShelf.svg';
import { navItemStyle, sideBarStyle } from './style';

const MSidebar = ({ isOpen }: { isOpen: boolean }) => {
    const translate = useTranslation();
    const { pathname } = useLocation();
    const companyInfo = useGetCompanyInfoSelector();
    const { authorizedTabs: userTabList } = useGetAuthorizedTabsSelector();
    const sideBarRef = useRef<null | HTMLDivElement>(null);
    const savedExpandedIndices = useRef<number[]>([]);
    const [expandedIndices, setExpandedIndices] = useState<number[]>([]);
    const globalPages: RouteDec = routesMapper.root.children![0];
    const authorizedTabs = ['home', ...userTabList];
    const hasEanKeywordsTabAccess = authorizedTabs.includes('product_search_score_v3');
    const displayGlobalCategoriesTab = authorizedTabs.includes('cdm_global_categories');
    const displayColorsTab = authorizedTabs.includes('cdm_colors');
    const displayKeywordEanTab = keys.ENVIRONMENT !== 'production' && hasEanKeywordsTabAccess;
    const displayKeywordCategoriesTab = keys.ENVIRONMENT !== 'production';
    const displayUserCompanyGroup = companyInfo?.has_company_group_allowed;

    if (!authorizedTabs.includes('cdm_international_company_groups')) {
        const userManagementRouteIndex = sideBarRoutes.findIndex((r) => r.id === 'cdm_usermanager');
        if (userManagementRouteIndex !== -1) {
            const userManagementRoute = sideBarRoutes[userManagementRouteIndex];
            const internationalCompanyGroupsRouteIndex = userManagementRoute.childs?.findIndex(
                (r) => r?.path === 'international-company-groups',
            );

            if (internationalCompanyGroupsRouteIndex !== -1 && internationalCompanyGroupsRouteIndex !== undefined) {
                sideBarRoutes[userManagementRouteIndex].childs![internationalCompanyGroupsRouteIndex].hide =
                    !authorizedTabs.includes('cdm_international_company_groups');
            }
        }
    }

    if (!authorizedTabs.includes('products')) {
        const clientsProductRouteIndex = sideBarRoutes.findIndex((r) => r.id === 'cdm_clientsproduct');
        if (clientsProductRouteIndex !== -1) {
            const clientsProductRoute = sideBarRoutes[clientsProductRouteIndex];
            const clientsProductRouteChildIndex = clientsProductRoute.childs?.findIndex((r) => r.path === '');

            if (clientsProductRouteChildIndex !== -1 && clientsProductRouteChildIndex !== undefined) {
                sideBarRoutes[clientsProductRouteIndex].childs![clientsProductRouteChildIndex].hide = true;
            }
        }
    }

    if (!authorizedTabs.includes('cdm_producttitlesplit')) {
        const clientsProductRouteIndex = sideBarRoutes.findIndex((r) => r.id === 'cdm_clientsproduct');
        if (clientsProductRouteIndex !== -1) {
            const clientsProductRoute = sideBarRoutes[clientsProductRouteIndex];
            const pathsToHide = ['splits', 'aliases', 'facultative'];

            pathsToHide.forEach((path) => {
                const childIndex = clientsProductRoute.childs?.findIndex((r) => r.path === path);
                if (childIndex !== -1 && childIndex !== undefined) {
                    sideBarRoutes[clientsProductRouteIndex].childs![childIndex].hide = true;
                }
            });
        }
    }

    if (!authorizedTabs.includes('cdm_pdp')) {
        const clientsProductRouteIndex = sideBarRoutes.findIndex((r) => r.id === 'cdm_clientsproduct');
        if (clientsProductRouteIndex !== -1) {
            const clientsProductRoute = sideBarRoutes[clientsProductRouteIndex];
            const pdpRouteChildIndex = clientsProductRoute.childs?.findIndex((r) => r.path === 'pdp');

            if (pdpRouteChildIndex !== -1 && pdpRouteChildIndex !== undefined) {
                sideBarRoutes[clientsProductRouteIndex].childs![pdpRouteChildIndex].hide = true;
            }
        }
    }

    const menuHideList: Record<string, boolean> = {
        'keywords-management/ean-keywords': !displayKeywordEanTab,
        'keywords-management/keywords-categories': !displayKeywordCategoriesTab,
        'user-management/company-group': !displayUserCompanyGroup,
        'preferences/global-categories': !displayGlobalCategoriesTab,
        'preferences/colors': !displayColorsTab,
    };

    const handleChanges = (val: number[]) => {
        setExpandedIndices(val);
        savedExpandedIndices.current = val;
    };

    const handleMouseEnter = () => {
        setExpandedIndices(savedExpandedIndices.current);
    };

    const handleMouseLeave = () => {
        setExpandedIndices([]);
    };

    useEffect(() => {
        isOpen ? handleMouseEnter() : handleMouseLeave();
    }, [isOpen]);

    const NavItem: FC<{ icon?: (props: any) => ReactNode; active: boolean; children?: ReactNode }> = ({
        icon,
        children,
        active,
    }) => {
        return (
            <Flex
                width={isOpen ? '285px' : '25px'}
                {...navItemStyle}
                color={active ? 'yellow.400' : 'gray.200'}
                borderColor={active ? 'yellow.400' : 'gray.200'}
                {...(!active ? { _hover: { color: 'white', borderColor: 'white' } } : {})}
            >
                {icon?.({ width: '24px', height: 'auto', marginRight: '18px' })}
                {children}
            </Flex>
        );
    };

    const renderSideBarRoutes = () => {
        return (
            <Accordion w="100%" index={expandedIndices} onChange={handleChanges} allowMultiple>
                {sideBarRoutes
                    .filter((r) => authorizedTabs.includes(r.id))
                    .sort((a, b) => a.order - b.order)
                    .map((route) => (
                        <Box key={route.id}>
                            {!route.childs?.filter(({ hide }) => !hide)?.length ? (
                                <Box as={Link} to={route.path}>
                                    <NavItem icon={globalPages[route.id].icon!} active={pathname.includes(route.path)}>
                                        <Text fontWeight="semibold" fontSize="md" whiteSpace="nowrap">
                                            {translate(globalPages[route.id].name || 'keyNotFound')}
                                        </Text>
                                    </NavItem>
                                </Box>
                            ) : (
                                <AccordionItem border="none">
                                    <AccordionButton p={0} _hover={{ backgroundColor: 'none' }}>
                                        <Box flexGrow={1}>
                                            <NavItem
                                                icon={globalPages[route.id].icon!}
                                                active={pathname.includes(route.path)}
                                            >
                                                <Text
                                                    fontWeight="semibold"
                                                    fontSize="md"
                                                    whiteSpace="nowrap"
                                                    flexGrow={1}
                                                    textAlign="left"
                                                >
                                                    {translate(globalPages[route.id].name || 'keyNotFound')}
                                                </Text>
                                                <AccordionIcon color="white" />
                                            </NavItem>
                                        </Box>
                                    </AccordionButton>
                                    <AccordionPanel p={0} pl={7}>
                                        {route.childs?.map(
                                            (subRoute) =>
                                                !subRoute.hide &&
                                                !menuHideList[`${route.path}/${subRoute.path}`] && (
                                                    <Box as={Link} key={subRoute.path} to={`${route.path}/${subRoute.path}`}>
                                                        <NavItem
                                                            active={pathname === `/${route.path}/${subRoute.path}`}
                                                        >
                                                            <Text
                                                                fontWeight="semibold"
                                                                fontSize="md"
                                                                whiteSpace="nowrap"
                                                            >
                                                                {translate(
                                                                    `${route.name}.subPage${
                                                                        subRoute.path ? `.${subRoute.path}` : ''
                                                                    }`,
                                                                )}
                                                            </Text>
                                                        </NavItem>
                                                    </Box>
                                                ),
                                        )}
                                    </AccordionPanel>
                                </AccordionItem>
                            )}
                        </Box>
                    ))}
            </Accordion>
        );
    };

    const SideBarOpenIcon = icons.sideBarOpen;

    return (
        <Box
            ref={sideBarRef}
            {...sideBarStyle}
            data-testid="app-sidebar"
            w={isOpen ? '320px' : '60px'}
            maxH="100vh"
            overflow="auto"
        >
            <Flex gap={'18px'} height={'80px'} paddingLeft={'18px'}>
                <Flex justifyContent={'center'} flexDirection={'column'}>
                    <SideBarOpenIcon
                        fontSize={'24px'}
                        color={'white'}
                        transform={isOpen ? 'scaleX(-1)' : ''}
                        transition={'all 0.3s ease'}
                        transitionDelay={'0.5s'}
                    />
                </Flex>
                <Flex
                    justifyContent={'center'}
                    flexDirection={'column'}
                    opacity={isOpen ? 1 : 0}
                    transition={'all 0.3s ease'}
                    transitionDelay={!isOpen ? '0.3s' : '0.5s'}
                >
                    <Image alignSelf={'left'} src={niqLogo} alt="NIQ logo" width={'220px'} minW={{ base: '198px' }} />
                </Flex>
            </Flex>
            <VStack alignItems="center" w="100%" px="18px">
                {renderSideBarRoutes()}
            </VStack>
        </Box>
    );
};

export default MSidebar;
